// The theme contract for the three visitor surfaces.
//
// Styles are data, not code paths. A surface never hard-codes a colour, a
// corner or a face: it reads CSS custom properties, and this module is what
// sets them. Adding a client is one TOKENS entry and one SHAPES entry.
// overrides={{ bg: '#…' }} skips even that.
//
// Two token families:
//   registration  ->  --bg / --ink / --accent / --line …
//   wheel, board  ->  --w-bg / --w-ink / --w-accent …
//
// The wheel and the board share one scope, so one client decision drives both.
// Brand is the look these surfaces ship. The other four systems stay so a
// later stand can switch without a fork.

export const SYSTEMS = ["xp1", "nova", "novaxp", "brand", "client"] as const;
export const MODES = ["light", "dark"] as const;

export type ThemeSystem = (typeof SYSTEMS)[number];
export type ThemeMode = (typeof MODES)[number];
export type ThemeSurface = "registration" | "wheel" | "leaderboard";

export const SYSTEM_LABELS: Readonly<Record<ThemeSystem, string>> = {
  xp1: "XP1",
  nova: "Nova",
  novaxp: "Nova XP",
  brand: "Brand",
  client: "Client",
};

export const SURFACE_SYSTEMS: Readonly<Record<ThemeSurface, readonly ThemeSystem[]>> = {
  registration: ["xp1", "nova", "novaxp", "brand", "client"],
  wheel: ["xp1", "nova", "novaxp", "brand", "client"],
  leaderboard: ["xp1", "nova", "novaxp", "brand", "client"],
};

export const SCOPE_OF: Readonly<Record<ThemeSurface, "reg" | "wheel">> = {
  registration: "reg",
  wheel: "wheel",
  leaderboard: "wheel",
};

export const SHIPPED_SYSTEM: ThemeSystem = "brand";

// The token sets, generated from the October hand-off's token-store.js
// (design_handoff_nova/prototype). The wheel takes its ground, ink, secondary,
// line, footer and field from the registration set of the same system, so one
// client reads the same on both; Client and Nova XP keep their own pin. The
// wheel's `data` (wheel.css's ticks) is the registration's, which the hand-off's
// own loop does not carry.
export const TOKENS: Readonly<Record<string, { readonly light: Record<string, string>; readonly dark: Record<string, string> }>> = {
  "reg-novaxp": {
    light: { canvas: "#f6f6f5", card: "#ffffff", wash: "#f0efed", line: "#dedcd8", lineSoft: "#e6e4e0", lineStrong: "#8d8b86", ink: "#232323", secondary: "#45433f", mute: "#6b6862", primary: "#232323", onPrimary: "#f6f6f5", button: "#ffffff", data: "#6d5a6b", tint: "#f1eeea", attention: "#5b4759", attentionWash: "#f4eff3", attentionLine: "#b9a3b7", footer: "#f0efed" },
    dark: { canvas: "#141310", card: "#1c1b17", wash: "#221f1a", line: "#2a2823", lineSoft: "#242220", lineStrong: "#6b6760", ink: "#edebe6", secondary: "#b8b4ac", mute: "#8f8b82", primary: "#edebe6", onPrimary: "#141310", button: "#1c1b17", data: "#c0a9be", tint: "#26231d", attention: "#c0a9be", attentionWash: "#221c22", attentionLine: "#4c3f4b", footer: "#191814" },
  },
  "reg-brand": {
    light: { canvas: "#f6f6f5", card: "#ffffff", wash: "#f0efed", line: "#dedcd8", lineSoft: "#e6e4e0", lineStrong: "#8d8b86", ink: "#232323", secondary: "#45433f", mute: "#6b6862", primary: "#232323", onPrimary: "#f6f6f5", button: "#ffffff", data: "#6d5a6b", tint: "#f1eeea", attention: "#5b4759", attentionWash: "#f4eff3", attentionLine: "#b9a3b7", footer: "#f0efed" },
    dark: { canvas: "#111111", card: "#191919", wash: "#1f1f1f", line: "#2a2a2a", lineSoft: "#242424", lineStrong: "#7a7a7a", ink: "#edebe6", secondary: "#b8b4ac", mute: "#8f8b82", primary: "#edebe6", onPrimary: "#111111", button: "#1e1e1e", data: "#c0a9be", tint: "#26231d", attention: "#c0a9be", attentionWash: "#221c22", attentionLine: "#4c3f4b", footer: "#161616" },
  },
  "reg-client": {
    light: { canvas: "#a8b290", card: "#f6f6f5", wash: "#b5be9f", line: "#939c7d", lineSoft: "#9da688", lineStrong: "#5d6450", ink: "#2b2b2b", secondary: "#3e3e3e", mute: "#3f4436", primary: "#ffb36b", onPrimary: "#2b2b2b", button: "#f6f6f5", data: "#3e3e3e", tint: "#b5be9f", attention: "#2b2b2b", attentionWash: "#f6f6f5", attentionLine: "#5d6450", footer: "#a8b290" },
    dark: { canvas: "#39412f", card: "#444d38", wash: "#414a36", line: "#525c45", lineSoft: "#4b543f", lineStrong: "#8a937a", ink: "#f1f0e8", secondary: "#dcdccf", mute: "#c2c4b2", primary: "#ffb36b", onPrimary: "#2b2b2b", button: "#444d38", data: "#ffb36b", tint: "#414a36", attention: "#ffb36b", attentionWash: "#444d38", attentionLine: "#8a937a", footer: "#323a29" },
  },
  "reg-nova": {
    light: { canvas: "#f3f3f3", card: "#ffffff", wash: "#ebebeb", line: "#dedede", lineSoft: "#e8e8e6", lineStrong: "#8b8b8b", ink: "#1a1a1a", secondary: "#474747", mute: "#666666", primary: "#1a1a1a", onPrimary: "#ffffff", button: "#fafafa", data: "#6d5a6b", tint: "#ece5eb", attention: "#5b4a5a", attentionWash: "#f2edf1", attentionLine: "#b9a3b7", footer: "#efefee" },
    dark: { canvas: "#111111", card: "#191919", wash: "#1f1f1f", line: "#2a2a2a", lineSoft: "#242424", lineStrong: "#7a7a7a", ink: "#f2f0f0", secondary: "#b8b8b8", mute: "#8f8f8f", primary: "#f2f0f0", onPrimary: "#111111", button: "#1e1e1e", data: "#b39fb1", tint: "#241f23", attention: "#bda5bb", attentionWash: "#191419", attentionLine: "#3f343e", footer: "#161616" },
  },
  "reg-xp1": {
    light: { canvas: "#f3f3f3", card: "#ffffff", wash: "#ebebeb", line: "#dedede", lineSoft: "#e8e8e6", lineStrong: "#8b8b8b", ink: "#1a1a1a", secondary: "#474747", mute: "#666666", primary: "#1a1a1a", onPrimary: "#ffffff", button: "#fafafa", data: "#6d5a6b", tint: "#ece5eb", attention: "#5b4a5a", attentionWash: "#f2edf1", attentionLine: "#b9a3b7", footer: "#efefee" },
    dark: { canvas: "#111111", card: "#191919", wash: "#1f1f1f", line: "#2a2a2a", lineSoft: "#242424", lineStrong: "#7a7a7a", ink: "#f2f0f0", secondary: "#b8b8b8", mute: "#8f8f8f", primary: "#f2f0f0", onPrimary: "#111111", button: "#1e1e1e", data: "#b39fb1", tint: "#241f23", attention: "#bda5bb", attentionWash: "#191419", attentionLine: "#3f343e", footer: "#161616" },
  },
  "wheel-xp1": {
    light: { bg: "#f3f3f3", ink: "#1a1a1a", accent: "#1a1a1a", accentInk: "#ffffff", logo: "#1a1a1a", field: "#ffffff", fieldInk: "#1a1a1a", secondary: "#474747", line: "#dedede", footer: "#efefee", data: "#6d5a6b" },
    dark: { bg: "#111111", ink: "#f2f0f0", accent: "#f2f0f0", accentInk: "#111111", logo: "#f2f0f0", field: "#191919", fieldInk: "#f2f0f0", secondary: "#b8b8b8", line: "#2a2a2a", footer: "#161616", data: "#b39fb1" },
  },
  "wheel-nova": {
    light: { bg: "#f3f3f3", ink: "#1a1a1a", accent: "#1a1a1a", accentInk: "#ffffff", logo: "#1a1a1a", field: "#ffffff", fieldInk: "#1a1a1a", secondary: "#474747", line: "#dedede", footer: "#efefee", data: "#6d5a6b" },
    dark: { bg: "#111111", ink: "#f2f0f0", accent: "#f2f0f0", accentInk: "#111111", logo: "#f2f0f0", field: "#191919", fieldInk: "#f2f0f0", secondary: "#b8b8b8", line: "#2a2a2a", footer: "#161616", data: "#b39fb1" },
  },
  "wheel-novaxp": {
    light: { bg: "#f6f6f5", ink: "#232323", accent: "#ff9000", accentInk: "#232323", logo: "#232323", field: "#ffffff", fieldInk: "#232323", secondary: "#45433f", line: "#dedcd8", footer: "#f0efed", data: "#6d5a6b" },
    dark: { bg: "#141310", ink: "#edebe6", accent: "#ff9000", accentInk: "#232323", logo: "#edebe6", field: "#1c1b17", fieldInk: "#edebe6", secondary: "#b8b4ac", line: "#2a2823", footer: "#191814", data: "#c0a9be" },
  },
  "wheel-brand": {
    light: { bg: "#f6f6f5", ink: "#232323", accent: "#232323", accentInk: "#f6f6f5", logo: "#232323", field: "#ffffff", fieldInk: "#232323", secondary: "#45433f", line: "#dedcd8", footer: "#f0efed", data: "#6d5a6b" },
    dark: { bg: "#111111", ink: "#edebe6", accent: "#edebe6", accentInk: "#111111", logo: "#edebe6", field: "#191919", fieldInk: "#edebe6", secondary: "#b8b4ac", line: "#2a2a2a", footer: "#161616", data: "#c0a9be" },
  },
  "wheel-client": {
    light: { bg: "#a8b290", ink: "#2b2b2b", accent: "#7a3600", accentInk: "#f6f6f5", logo: "#2b2b2b", field: "#f6f6f5", fieldInk: "#2b2b2b", secondary: "#3e3e3e", line: "#939c7d", footer: "#a8b290", data: "#3e3e3e" },
    dark: { bg: "#39412f", ink: "#f1f0e8", accent: "#ffb36b", accentInk: "#2b2b2b", logo: "#f1f0e8", field: "#444d38", fieldInk: "#f1f0e8", secondary: "#dcdccf", line: "#525c45", footer: "#323a29", data: "#ffb36b" },
  },
};

export const SHAPES: Readonly<Record<string, Readonly<Record<string, string>>>> = {
  "reg-novaxp": { ctlRadius: "999px", cardRadius: "18px", borderWidth: "1px", elevation: "-2px 3px 8px rgba(26,26,26,.10)", ui: "\"Montserrat\", Helvetica, Arial, sans-serif", mono: "\"JetBrains Mono\", ui-monospace, monospace" },
  "reg-brand": { ctlRadius: "999px", fieldRadius: "8px", tickRadius: "8px", fieldBorder: "1px", cardRadius: "18px", borderWidth: "1px", elevation: "-2px 3px 8px rgba(26,26,26,.10)", ui: "\"Montserrat\", Helvetica, Arial, sans-serif", mono: "\"JetBrains Mono\", ui-monospace, monospace" },
  "reg-client": { ctlRadius: "999px", fieldRadius: "8px", tickRadius: "8px", fieldBorder: "1px", cardRadius: "18px", borderWidth: "1px", elevation: "-2px 3px 8px rgba(26,26,26,.10)", ui: "\"Montserrat\", Helvetica, Arial, sans-serif", mono: "\"JetBrains Mono\", ui-monospace, monospace" },
  "reg-nova": { ctlRadius: "999px", cardRadius: "18px", borderWidth: "1px", elevation: "-2px 3px 8px rgba(26,26,26,.10)", ui: "\"Montserrat\", Helvetica, Arial, sans-serif", mono: "\"JetBrains Mono\", ui-monospace, monospace" },
  "reg-xp1": { ctlRadius: "8px", fieldRadius: "8px", fieldBorder: "1px", cardRadius: "12px", borderWidth: "2px", elevation: "none", ui: "\"Montserrat\", Helvetica, Arial, sans-serif", mono: "\"JetBrains Mono\", ui-monospace, monospace" },
  "wheel-xp1": { ctlRadius: "8px", cardRadius: "4px", headFont: "\"Montserrat\", Helvetica, Arial, sans-serif", bodyFont: "\"Montserrat\", Helvetica, Arial, sans-serif" },
  "wheel-nova": { ctlRadius: "999px", cardRadius: "8px", headFont: "\"Montserrat\", Helvetica, Arial, sans-serif", bodyFont: "\"Montserrat\", Helvetica, Arial, sans-serif" },
  "wheel-novaxp": { ctlRadius: "999px", cardRadius: "8px", headFont: "\"Montserrat\", Helvetica, Arial, sans-serif", bodyFont: "\"Montserrat\", Helvetica, Arial, sans-serif" },
  "wheel-brand": { ctlRadius: "999px", fieldRadius: "8px", tickRadius: "8px", cardRadius: "8px", headFont: "\"Montserrat\", Helvetica, Arial, sans-serif", bodyFont: "\"Montserrat\", Helvetica, Arial, sans-serif" },
  "wheel-client": { ctlRadius: "999px", fieldRadius: "8px", tickRadius: "8px", cardRadius: "8px", headFont: "\"Montserrat\", Helvetica, Arial, sans-serif", bodyFont: "\"Montserrat\", Helvetica, Arial, sans-serif" },
};

const COLOUR_VARS: Readonly<Record<"reg" | "wheel", Readonly<Record<string, readonly string[]>>>> = {
  reg: {
    canvas: ["--bg"],
    card: ["--surface"],
    wash: ["--surface-2"],
    line: ["--line"],
    lineSoft: ["--line-soft"],
    lineStrong: ["--line-strong"],
    ink: ["--ink"],
    secondary: ["--ink-2"],
    mute: ["--ink-3"],
    primary: ["--accent"],
    onPrimary: ["--accent-ink"],
    button: ["--btn"],
    data: ["--data"],
    tint: ["--tint"],
    attention: ["--warn"],
    attentionWash: ["--warn-bg"],
    attentionLine: ["--warn-line"],
    footer: ["--foot"],
  },
  wheel: {
    bg: ["--w-bg"],
    ink: ["--w-ink"],
    accent: ["--w-accent"],
    accentInk: ["--w-accent-ink"],
    logo: ["--w-logo"],
    field: ["--w-field"],
    fieldInk: ["--w-field-ink"],
    data: ["--w-data"],
    secondary: ["--w-secondary"],
    line: ["--w-line"],
    footer: ["--w-foot"],
  },
};

const SHAPE_VARS: Readonly<Record<"reg" | "wheel", Readonly<Record<string, readonly string[]>>>> = {
  reg: {
    ctlRadius: ["--ctl-r"],
    // A field's and a tick box's corner, apart from a button's: Brand's
    // buttons are pills and its fields and ticks are square with rounded
    // corners (the October brief).
    fieldRadius: ["--fld-r"],
    tickRadius: ["--tick-r"],
    fieldBorder: ["--fld-bw"],
    cardRadius: ["--card-r"],
    borderWidth: ["--bd-w"],
    elevation: ["--lift"],
    ui: ["--reg-font"],
    mono: ["--reg-mono"],
  },
  wheel: {
    ctlRadius: ["--w-r-ctl"],
    fieldRadius: ["--w-r-fld"],
    tickRadius: ["--w-r-tick"],
    cardRadius: ["--w-r-box"],
    headFont: ["--w-font-h"],
    bodyFont: ["--w-font-b"],
  },
};

export function scopeKey(surface: ThemeSurface, system: ThemeSystem): string {
  return `${SCOPE_OF[surface]}-${system}`;
}

export function themeVars(
  surface: ThemeSurface,
  system: ThemeSystem,
  mode: ThemeMode,
  overrides?: Readonly<Record<string, string>> | undefined,
): Record<string, string> {
  const family = SCOPE_OF[surface];
  const key = scopeKey(surface, system);
  const colours = { ...(TOKENS[key]?.[mode] ?? {}), ...(overrides ?? {}) };
  const shape = SHAPES[key] ?? {};
  const out: Record<string, string> = {};
  for (const [name, vars] of Object.entries(COLOUR_VARS[family])) {
    const value = colours[name];
    if (value !== undefined) for (const token of vars) out[token] = value;
  }
  for (const [name, vars] of Object.entries(SHAPE_VARS[family])) {
    const value = shape[name];
    if (value !== undefined) for (const token of vars) out[token] = value;
  }
  return out;
}

export function applyTheme(
  el: HTMLElement | null,
  surface: ThemeSurface,
  system: ThemeSystem,
  mode: ThemeMode,
  overrides?: Readonly<Record<string, string>> | undefined,
): void {
  if (el === null) return;
  const vars = themeVars(surface, system, mode, overrides);
  for (const [name, value] of Object.entries(vars)) el.style.setProperty(name, value);
}

export function contrast(a: string, b: string): number {
  const lum = (hex: string): number => {
    const m = hex.replace("#", "");
    const n = m.length === 3 ? m.split("").map((c) => c + c) : m.match(/../g);
    if (n === null) return 0;
    const lin = n.map((h) => {
      const v = parseInt(h, 16) / 255;
      return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * (lin[0] ?? 0) + 0.7152 * (lin[1] ?? 0) + 0.0722 * (lin[2] ?? 0);
  };
  const l1 = lum(a);
  const l2 = lum(b);
  return +(((Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05)).toFixed(2));
}

export type ThemeCheck = {
  readonly what: string;
  readonly got: number;
  readonly floor: number;
  readonly passes: boolean;
};

export function checkTheme(
  surface: ThemeSurface,
  system: ThemeSystem,
  mode: ThemeMode,
  overrides?: Readonly<Record<string, string>> | undefined,
): ThemeCheck[] {
  const v = { ...(TOKENS[scopeKey(surface, system)]?.[mode] ?? {}), ...(overrides ?? {}) };
  if (SCOPE_OF[surface] === "reg") {
    return (
      [
        ["ink on canvas", contrast(v["ink"] ?? "", v["canvas"] ?? ""), 4.5],
        ["button label", contrast(v["onPrimary"] ?? "", v["primary"] ?? ""), 4.5],
      ] as const
    ).map(([what, got, floor]) => ({ what, got, floor, passes: got >= floor }));
  }
  return (
    [
      ["ink on ground", contrast(v["ink"] ?? "", v["bg"] ?? ""), 4.5],
      ["typing in a field", contrast(v["fieldInk"] ?? "", v["field"] ?? ""), 4.5],
      ["button label", contrast(v["accentInk"] ?? "", v["accent"] ?? ""), 4.5],
      ["pin on ground", contrast(v["accent"] ?? "", v["bg"] ?? ""), 3],
    ] as const
  ).map(([what, got, floor]) => ({ what, got, floor, passes: got >= floor }));
}

/** Brand tokens mapped onto the --xp-* names the public page already reads. */
export const BRAND_XP_TOKENS: Readonly<Record<ThemeMode, Readonly<Record<string, string>>>> = {
  light: {
    "--xp-bg": "#f6f6f5",
    "--xp-surface": "#ffffff",
    "--xp-surface-2": "#f0efed",
    "--xp-line": "#dedcd8",
    "--xp-line-strong": "#8d8b86",
    "--xp-ink": "#232323",
    "--xp-ink-2": "#45433f",
    "--xp-ink-3": "#6b6862",
    "--xp-accent": "#232323",
    "--xp-accent-ink": "#f6f6f5",
    "--xp-data": "#6d5a6b",
    "--xp-tint": "#f1eeea",
    "--xp-radius": "999px",
    "--xp-radius-sm": "8px",
    "--xp-sans": '"Montserrat", "Helvetica Neue", Helvetica, Arial, sans-serif',
    "--xp-shadow": "-2px 3px 8px rgba(26,26,26,.10)",
    "--xp-foot": "#f0efed",
    "--xp-warn": "#5b4759",
    "--xp-warn-soft": "#f4eff3",
  },
  // reg-brand dark, as the October hand-off has it.
  dark: {
    "--xp-bg": "#111111",
    "--xp-surface": "#191919",
    "--xp-surface-2": "#1f1f1f",
    "--xp-line": "#2a2a2a",
    "--xp-line-strong": "#7a7a7a",
    "--xp-ink": "#edebe6",
    "--xp-ink-2": "#b8b4ac",
    "--xp-ink-3": "#8f8b82",
    "--xp-accent": "#edebe6",
    "--xp-accent-ink": "#111111",
    "--xp-data": "#c0a9be",
    "--xp-tint": "#26231d",
    "--xp-radius": "999px",
    "--xp-radius-sm": "8px",
    "--xp-sans": '"Montserrat", "Helvetica Neue", Helvetica, Arial, sans-serif',
    "--xp-shadow": "-2px 3px 8px rgba(0,0,0,.45)",
    "--xp-foot": "#161616",
    "--xp-warn": "#c0a9be",
    "--xp-warn-soft": "#221c22",
  },
};
